import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { PagesID } from "@utils/utilsConfigurations";
import { ServicesURL, downloadBlobFile } from "@utils/utilsApi";
import { openToast } from "@utils/toastStore";
import ConfirmDialogCst from "@components/ConfirmDialogCst";
import { fetcher } from "src/services/config";
import { MethodType } from "src/services/type";
import { closeUsersAndGroupsModal } from "../store";
import EnrollmentListShell from "../components/EnrollmentListShell";
import EnrollmentHistoryModal from "../components/EnrollmentHistoryModal";
import EditEnrollmentModal, {
  buildConfigFromEnrollment,
  disabledByPriority,
} from "../components/EditEnrollmentModal";
import EnrollLOModal from "@pages/Contents/V2/Corporate/UsersGroups/components/EnrollLOModal";
import { EnrollType } from "@pages/Contents/V2/Corporate/Catalog/Enrollment/types";
import UserEnrollmentRow from "../components/UserEnrollmentRow";
import { usePreEnrolledUsersList } from "../hooks/usePreEnrolledUsersList";
import { EnrolledUserItem } from "../types";
import { CatalogEntityType } from "@pages/Contents/V2/Corporate/Catalog/types";

const PAGE_SIZE = 10;

interface PreEnrolledUsersTabProps {
  corporateId: string | number;
  objectId: number;
  objectType: CatalogEntityType;
  objectTitle?: string;
}

const PreEnrolledUsersTab: React.FC<PreEnrolledUsersTabProps> = ({
  corporateId,
  objectId,
  objectType,
  objectTitle,
}) => {
  const { t } = useTranslation("users-and-groups");
  const { t: tInput } = useTranslation("input");
  const navigate = useNavigate();

  const statusOptions = ["pa", "pw", "r"].map((key) => ({
    value: key.toUpperCase(),
    label: String(tInput(`options.status_enrollment.${key}`)),
  }));
  const enrollTypeOptions = ["pre_auto", "pre_bottom"].map((key) => ({
    value: key.toUpperCase(),
    label: String(tInput(`options.enroll_type.${key}`)),
  }));
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(PAGE_SIZE);
  const [search, setSearch] = useState("");
  const [fieldFilters, setFieldFilters] = useState<Record<string, any>>({});
  const [deleteTarget, setDeleteTarget] = useState<{
    user: EnrolledUserItem;
    scope: "single" | "group";
  } | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const [historyTarget, setHistoryTarget] = useState<EnrolledUserItem | null>(
    null,
  );
  const [editTarget, setEditTarget] = useState<EnrolledUserItem | null>(null);
  const [editGroupTarget, setEditGroupTarget] =
    useState<EnrolledUserItem | null>(null);
  const [groupEnrollSource, setGroupEnrollSource] =
    useState<EnrolledUserItem | null>(null);

  const { items, totalItems, totalPages, isLoading, mutate } =
    usePreEnrolledUsersList({
      corporateId,
      objectId,
      page,
      pageSize,
      search: search || undefined,
      fieldFilters,
    });

  const goToUserDetail = (user: EnrolledUserItem) => {
    if (!user.userId) return;
    closeUsersAndGroupsModal();
    navigate(`/v2/corporate/${corporateId}/detail/users-groups/${user.userId}`);
  };

  const confirmDelete = async () => {
    if (!deleteTarget) return;
    const { user, scope } = deleteTarget;
    if (scope === "single" && !user.enrollId) return;
    if (scope === "group" && (user.massiveGroupId == null || !user.groupType))
      return;
    setIsDeleting(true);
    try {
      const url =
        scope === "group"
          ? `${ServicesURL.postEnrollmentMassive}?corporateId=${corporateId}&groupId=${user.massiveGroupId}&groupType=${user.groupType}&objectId=${objectId}`
          : `${ServicesURL.deleteEnrollment}?enrollmentEId=${user.enrollId}`;
      await fetcher(url, MethodType.DELETE);
      setDeleteTarget(null);
      mutate();
      openToast({
        severity: "success",
        message: String(t("card.delete-confirm.success")),
      });
    } catch (error) {
      console.error(error);
      openToast({
        severity: "error",
        message: String(t("card.delete-confirm.error")),
      });
    } finally {
      setIsDeleting(false);
    }
  };

  const downloadCertificate = async (user: EnrolledUserItem) => {
    if (user.certificateId == null || !user.userId) return;
    try {
      const url = `${ServicesURL.getIssuedCertificate.replace(
        ":certificateId",
        String(user.certificateId),
      )}?objectId=${objectId}&learnerId=${user.userId}`;
      await downloadBlobFile(
        url,
        null,
        `issued_certificate_lo_${objectId}_learner_${user.userId}.pdf`,
        MethodType.GET,
      );
    } catch (error) {
      console.error(error);
      openToast({
        severity: "error",
        message: String(t("card.certificate-error")),
      });
    }
  };

  const deleteTargetName = deleteTarget
    ? [deleteTarget.user.userFirstName, deleteTarget.user.userLastName]
        .filter(Boolean)
        .join(" ")
        .trim() || "-"
    : "";

  return (
    <EnrollmentListShell
      search={search}
      onSearchSubmit={(v) => {
        setSearch(v);
        setPage(1);
      }}
      hideSearch
      filtersPageId={PagesID["corporate.catalog.courses.subscribed"]}
      filtersExcludeKeys={["blendedParentId"]}
      filtersDynamicOptions={{
        status: statusOptions,
        enrollType: enrollTypeOptions,
      }}
      fieldFilters={fieldFilters}
      onApplyFilters={(next) => {
        setFieldFilters(next);
        setPage(1);
      }}
      isLoading={isLoading}
      isEmpty={items.length === 0}
      emptyLabel={String(t("empty.pre-enrolled-users"))}
      page={page}
      pageSize={pageSize}
      totalItems={totalItems}
      totalPages={totalPages}
      onPageChange={setPage}
      onPageSizeChange={(next) => {
        setPageSize(next);
        setPage(1);
      }}
    >
      {items.map((user) => (
        <UserEnrollmentRow
          key={`${user.userId}-${user.enrollId ?? ""}`}
          user={user}
          onDetails={goToUserDetail}
          onEditEnrollment={(u) => setEditTarget(u)}
          onEditGroupEnrollment={(u) => setEditGroupTarget(u)}
          onDeleteEnrollment={(u) =>
            setDeleteTarget({ user: u, scope: "single" })
          }
          onDeleteGroupEnrollment={(u) =>
            setDeleteTarget({ user: u, scope: "group" })
          }
          onHistory={(u) => setHistoryTarget(u)}
          onDownloadCertificate={downloadCertificate}
        />
      ))}

      <ConfirmDialogCst
        open={!!deleteTarget}
        title={String(
          t(
            deleteTarget?.scope === "group"
              ? "card.delete-group-confirm.title"
              : "card.delete-confirm.title",
          ),
        )}
        description={String(
          t(
            deleteTarget?.scope === "group"
              ? "card.delete-group-confirm.description"
              : "card.delete-confirm.description",
            { name: deleteTargetName },
          ),
        )}
        confirmLabel={String(t("card.delete-confirm.confirm"))}
        cancelLabel={String(t("card.delete-confirm.cancel"))}
        confirmVariant="danger"
        isSubmitting={isDeleting}
        onClose={() => setDeleteTarget(null)}
        onConfirm={confirmDelete}
      />

      <EnrollmentHistoryModal
        open={!!historyTarget}
        onClose={() => setHistoryTarget(null)}
        objectId={objectId}
        objectTitle={objectTitle}
        user={historyTarget}
      />

      <EditEnrollmentModal
        open={!!editTarget}
        onClose={() => setEditTarget(null)}
        corporateId={corporateId}
        objectId={objectId}
        objectType={objectType}
        objectTitle={objectTitle}
        user={editTarget}
        onSuccess={mutate}
      />

      <ConfirmDialogCst
        open={!!editGroupTarget}
        title={String(t("card.edit-group-confirm.title"))}
        description={String(
          t("card.edit-group-confirm.description", {
            groupId: editGroupTarget?.massiveGroupId ?? "-",
          }),
        )}
        confirmLabel={String(t("card.edit-group-confirm.confirm"))}
        cancelLabel={String(t("card.delete-confirm.cancel"))}
        onClose={() => setEditGroupTarget(null)}
        onConfirm={() => {
          setGroupEnrollSource(editGroupTarget);
          setEditGroupTarget(null);
        }}
      />

      <EnrollLOModal
        open={!!groupEnrollSource}
        onClose={() => setGroupEnrollSource(null)}
        subjectType="group"
        subject={
          groupEnrollSource
            ? {
                id: groupEnrollSource.massiveGroupId,
                name:
                  groupEnrollSource.groupName ||
                  `ID ${groupEnrollSource.massiveGroupId}`,
              }
            : null
        }
        groupType={groupEnrollSource?.groupType ?? "STATIC"}
        corporateId={String(corporateId)}
        onSuccess={mutate}
        initialStep="config"
        initialSelection={
          groupEnrollSource
            ? [
                {
                  id: objectId,
                  kind: objectType,
                  title: objectTitle ?? "",
                  entity: {},
                },
              ]
            : []
        }
        initialConfig={
          groupEnrollSource
            ? buildConfigFromEnrollment(groupEnrollSource)
            : undefined
        }
        extraDisabledEnrollTypes={disabledByPriority(
          groupEnrollSource?.enrollType as EnrollType | undefined,
        )}
      />
    </EnrollmentListShell>
  );
};

export default PreEnrolledUsersTab;
